/** @module csi/table/_GroupByRefMixin */
define([
	"dojo/_base/declare",
	"csi/core/Key",
	"csi/core/SpecBuilder",
	"csi/def/Heading",
	"csi/locale",
], function(declare, Key, SpecBuilder, Heading, locale) {

	/**
	 * _GroupByRefMixin is used when you want to group your view by a ref attribute and it is possible the
	 * referenced nodes do not have unique names. When this is done, the view will be grouped by default
	 * and the grouping cannot be changed in the custom view unless optional.
	 */

	return declare(null, {
		bDisableNewActionIfNoGroups: true,

		/** @property {string} groupByRefAttr - ref attribute to group by */
		groupByRefAttr: null,

		/** @property {boolean} groupByRefOptional - indicates if grouping by ref is optional */
		groupByRefOptional: false,

		/** @property {Spec} _groupByRefSpec - spec used for grouping */
		_groupByRefSpec: null,

		/**
		 * Disable grouping in view unless optional
		 */
		canGroup() {
			return this.groupByRefOptional;
		},

		/**
		 * @override
		 * Check grouping spec too
		 */
		getHeadingSpec(key, ignoreMatrixGroup) {
			if (this._groupByRefSpec?.matchesKey(key)) {
				return this._groupByRefSpec;
			}

			return this.inherited(arguments);
		},

		/**
		 * @override
		 * Table view to group by parent Affected Item
		 */
		onCreateDefaultCustomView(cv) {
			const {groupByRefOptional, _defaultCustomView, _groupByRefSpec} = this;

			this.inherited(arguments);

			if (_groupByRefSpec && groupByRefOptional) {
				_defaultCustomView.setValue("groupSettings", {key1: _groupByRefSpec.uniqueId, expand1: true});
			}
		},

		/**
		 * @override
		 * Add custom group spec to options
		 */
		onCustomViewDataManagementOptions(options) {
			this.inherited(arguments);
			options.customGroupSpec = this._groupByRefSpec;
		},

		/**
		 * @override
		 * Group by section by default if not optional - if optional, then groupSettings will be initialized for default custom view
		 */
		onViewPreferences() {
			const {groupByRefOptional, _groupByRefSpec} = this;

			this.inherited(arguments);

			if (_groupByRefSpec && !groupByRefOptional) {
				this.set("groupSettings", {key1: _groupByRefSpec.uniqueId, expand1: true});
			}
		},

		/**
		 * Configure query level for group
		 * @param {Level} ql
		 */
		onGroupByRefQueryLevel(ql) { },

		/**
		 * Call extension point to customize group query level
		 */
		_createQueryLevel(params, bRoot) {
			const {_groupByRefSpec} = this;
			const ql = this.inherited(arguments);

			if (_groupByRefSpec && (ql.groupSpec === _groupByRefSpec)) {
				this.onGroupByRefQueryLevel(ql);

				// a groupDefaultStore is required to group by ref so that the group labels are known
				console.assert(!!ql.groupDefaultsStore);
			}

			return ql;
		},

		/**
		 * @override
		 * Add special CSS when rendering group placeholders, and special behavior when grouping by the parent
		 */
		_resolveView() {
			const {defView, groupByRefAttr} = this;
			const rc = defView.getPageClass();
			const specBuilder = new SpecBuilder(rc, this.getContext());
			const heading = Object.assign(new Heading(), {
				m_id: groupByRefAttr,
				m_name: Key.GROUP_BY_REF_NAME,
				classRendererParams: {bQueryByRef: true},
			});

			this._groupByRefSpec = specBuilder.getSpecForHeading(heading);
			this._groupByRefSpec.name = locale.typeId(rc.type, groupByRefAttr);
			this.inherited(arguments);
		}
	});
});